import { useEffect } from "react";
import moment from "moment";
import {
  Chip,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useDispatch, useSelector } from "react-redux";
import Loader from "@jumbo/components/Loader";
import {
  clearNotificationDetail,
  getSupplierNotificationById,
} from "app/redux/slice/supplierNotificationSlice";

const Field = ({ label, children }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">{children}</Typography>
  </Grid>
);

// Read receipts and the exact text each vendor was shown. The message is
// rendered per recipient on the server, because {{price}} is that vendor's own
// party rate as it stood when the notification went out.
const ViewNotification = ({ id, onClose }) => {
  const dispatch = useDispatch();
  const { detail, recipients, detailLoading } = useSelector(
    (state) => state?.supplierNotification,
  );

  useEffect(() => {
    if (id) dispatch(getSupplierNotificationById(id));
    return () => {
      dispatch(clearNotificationDetail());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (detailLoading) return <Loader />;
  if (!detail) return <Typography>Notification not found.</Typography>;

  return (
    <>
      <Grid container spacing={2}>
        <Field label="Title">{detail.title}</Field>
        <Field label="Type">
          <Chip size="small" label={detail.type} />
        </Field>
        <Field label="Product">{detail.productName || "—"}</Field>
        <Field label="Audience">
          {detail.audience === "PRODUCT_RATE"
            ? `Vendors with a rate on this product (${detail.totalRecipients || 0})`
            : detail.isGlobal !== false
              ? "All Approved vendors"
              : `${detail.assignedSuppliers?.length || 0} selected vendor(s)`}
        </Field>
        <Field label="Sent By">
          {detail.source === "AUTO"
            ? `Automatic (${detail.eventKey})`
            : detail.createdBy?.name || "—"}
        </Field>
        <Field label="Sent On">
          {moment(detail.sentAt || detail.createdAt).format(
            "DD-MM-YYYY hh:mm A",
          )}
        </Field>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">
            Message template
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {detail.message}
          </Typography>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
        <Typography variant="h5" mb={0}>
          Recipients
        </Typography>
        <Chip
          size="small"
          color="info"
          label={`${detail.readCount || 0} / ${detail.totalRecipients || 0} read`}
        />
      </Stack>

      <TableContainer sx={{ maxHeight: 320 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Vendor</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Message</TableCell>
              <TableCell align="center">Read</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recipients?.length ? (
              recipients.map((recipient) => (
                <TableRow key={recipient._id}>
                  <TableCell>{recipient.supplierName}</TableCell>
                  <TableCell>
                    {recipient.price === null || recipient.price === undefined
                      ? "—"
                      : `₹${recipient.price}`}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    {recipient.message}
                  </TableCell>
                  <TableCell align="center">
                    {recipient.readAt ? (
                      <Chip
                        size="small"
                        color="success"
                        label={moment(recipient.readAt).format("DD-MM-YYYY")}
                      />
                    ) : (
                      <Chip size="small" label="Unread" />
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No recipients.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
        <LoadingButton variant="outlined" color="secondary" onClick={onClose}>
          Close
        </LoadingButton>
      </Stack>
    </>
  );
};

export default ViewNotification;
